import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Stack,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Divider,
  Switch,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, TrendingUp, TrendingDown } from '@mui/icons-material';
import { UserContext } from '../../../components/shared/useUser';
import {
  getCashTransactionClient,
  CashReconciliationDto,
  CloseTillRequest,
  DenominationBreakdownDto,
  formatCashAmount,
} from '../../../app/services/api/cashTransactions';
import useLogError from '../../../hooks/useLogError';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import PageArea from '../../../components/shared/PageArea';
import BackButton from '../../../components/shared/actions/BackButton';
import NumberPad from './components/NumberPad';
import DenominationBreakdown from '../components/DenominationBreakdown';

/**
 * Mobile-optimized till counting screen
 * Simplified workflow with quick total by default
 */
const MobileCountScreen: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('cashReconciliation');
  const { tillNumber } = useParams<{ tillNumber: string }>();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const cashClient = getCashTransactionClient();

  const [loading, setLoading] = useState(false);
  const [reconciliation, setReconciliation] = useState<CashReconciliationDto | null>(null);
  const [useBreakdown, setUseBreakdown] = useState(false);
  const [quickTotal, setQuickTotal] = useState('0');
  const [breakdown, setBreakdown] = useState<DenominationBreakdownDto | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    loadReconciliation();
  }, [tillNumber]);

  const loadReconciliation = async () => {
    if (!tillNumber || !selectedStore?.storeNumber) return;

    setLoading(true);
    try {
      // Get till state to find active reconciliation
      const tillState = await cashClient.getTillState(selectedStore.storeNumber, tillNumber);

      if (tillState.lastReconciliationId) {
        const recon = await cashClient.getCashReconciliationById(tillState.lastReconciliationId);
        setReconciliation(recon);
      }
    } catch (err) {
      logError(err);
      setError(t('errors.loadDataFailed', 'Failed to load till data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!reconciliation) return;

    setError('');

    const declaredAmount = useBreakdown
      ? breakdown?.grandTotal || 0
      : parseFloat(quickTotal) || 0;

    if (declaredAmount <= 0) {
      setError(t('errors.invalidDeclaredAmount', 'Please enter a valid cash amount'));
      return;
    }

    const variance = declaredAmount - reconciliation.expectedClosingBalance;
    const requiresNotes = Math.abs(variance) > 1;

    if (requiresNotes && !notes.trim()) {
      setError(t('errors.explainVarianceRequired', 'Please explain the variance'));
      return;
    }

    try {
      setLoading(true);

      const request: CloseTillRequest = {
        reconciliationId: reconciliation.id,
        declaredClosingBalance: declaredAmount,
        denominationBreakdown: useBreakdown ? breakdown : undefined,
        countMethod: useBreakdown ? 'DenominationBreakdown' : 'QuickTotal',
        notes: notes.trim() || undefined,
      };

      await cashClient.closeTill(request);

      setShowSuccess(true);
      setTimeout(() => {
        navigate('/cash-reconciliation/mobile');
      }, 1500);
    } catch (err) {
      logError(err);
      setError(t('errors.submitCountFailed', 'Failed to submit count. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!reconciliation) {
    return (
      <PageArea>
        <Stack spacing={2}
          alignItems="center"
          py={8}>
          {loading && <LinearProgress sx={{ width: '100%' }} />}
          <Typography>{t('mobile.loadingTillData', 'Loading till data...')}</Typography>
        </Stack>
      </PageArea>
    );
  }

  const declaredAmount = useBreakdown
    ? breakdown?.grandTotal || 0
    : parseFloat(quickTotal) || 0;

  const variance = declaredAmount - reconciliation.expectedClosingBalance;
  const hasVariance = Math.abs(variance) > 0.01;
  const isOverThreshold = Math.abs(variance) > 10; // TODO: Use zone threshold

  return (
    <PageArea>
      <Box sx={{ pb: 4 }}>
        {/* Header */}
        <Stack spacing={2}
          mb={3}>
          <BackButton url="/cash-reconciliation/mobile"
            title={t('common.back', 'Back')}
            isLink />
          <Typography variant="displayMD"
            fontWeight={700}>
            {t('mobile.countScreen', 'Count {{tillNumber}}', { tillNumber })}
          </Typography>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && <Alert severity="error"
          sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={3}>
          {/* Expected Breakdown Card */}
          <Card sx={{ p: 3, bgcolor: theme.palette.custom.gray[50] }}>
            <Stack spacing={2}>
              <Typography variant="textMD"
                fontWeight={600}>
                {t('mobile.expectedInTill', 'Expected in Till')}
              </Typography>
              <Typography variant="displayLG"
                fontWeight={700}
                color="primary">
                {formatCashAmount(reconciliation.expectedClosingBalance, 'GBP')}
              </Typography>
              <Divider />
              <Stack spacing={1}>
                <Stack direction="row"
                  justifyContent="space-between">
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('mobile.openingFloat', 'Opening Float')}:
                  </Typography>
                  <Typography variant="textSM"
                    fontWeight={600}>
                    {formatCashAmount(reconciliation.openingBalance, 'GBP')}
                  </Typography>
                </Stack>
                {reconciliation.expectedCashSales > 0 && (
                  <Stack direction="row"
                    justifyContent="space-between">
                    <Stack direction="row"
                      spacing={0.5}
                      alignItems="center">
                      <TrendingUp fontSize="small"
                        sx={{ color: 'success.main' }} />
                      <Typography variant="textSM"
                        color="text.secondary">
                        {t('mobile.cashSales', 'Cash Sales')}:
                      </Typography>
                    </Stack>
                    <Typography variant="textSM"
                      fontWeight={600}
                      color="success.main">
                      +{formatCashAmount(reconciliation.expectedCashSales, 'GBP')}
                    </Typography>
                  </Stack>
                )}
                {reconciliation.expectedPaidIns > 0 && (
                  <Stack direction="row"
                    justifyContent="space-between">
                    <Typography variant="textSM"
                      color="text.secondary">
                      {t('mobile.paidIns', 'Paid Ins')}:
                    </Typography>
                    <Typography variant="textSM"
                      fontWeight={600}
                      color="success.main">
                      +{formatCashAmount(reconciliation.expectedPaidIns, 'GBP')}
                    </Typography>
                  </Stack>
                )}
                {reconciliation.expectedPaidOuts > 0 && (
                  <Stack direction="row"
                    justifyContent="space-between">
                    <Stack direction="row"
                      spacing={0.5}
                      alignItems="center">
                      <TrendingDown fontSize="small"
                        sx={{ color: 'error.main' }} />
                      <Typography variant="textSM"
                        color="text.secondary">
                        {t('mobile.paidOuts', 'Paid Outs')}:
                      </Typography>
                    </Stack>
                    <Typography variant="textSM"
                      fontWeight={600}
                      color="error.main">
                      -{formatCashAmount(reconciliation.expectedPaidOuts, 'GBP')}
                    </Typography>
                  </Stack>
                )}
                {reconciliation.expectedCashDrops > 0 && (
                  <Stack direction="row"
                    justifyContent="space-between">
                    <Typography variant="textSM"
                      color="text.secondary">
                      {t('mobile.cashDrops', 'Cash Drops')}:
                    </Typography>
                    <Typography variant="textSM"
                      fontWeight={600}
                      color="error.main">
                      -{formatCashAmount(reconciliation.expectedCashDrops, 'GBP')}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Card>

          {/* Count Method Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={useBreakdown}
                onChange={(e) => setUseBreakdown(e.target.checked)}
              />
            }
            label={t('mobile.countByDenomination', 'Count by denomination')}
            sx={{ justifyContent: 'center', m: 0 }}
          />

          {/* Count Entry */}
          {!useBreakdown ? (
            <Card sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="textMD"
                  fontWeight={600}>
                  {t('mobile.totalCashCounted', 'Total Cash Counted')}
                </Typography>
                <NumberPad
                  value={quickTotal}
                  onChange={setQuickTotal}
                  presets={[100, 200, 300, 500]}
                />
              </Stack>
            </Card>
          ) : (
            <Card sx={{ p: 3 }}>
              <DenominationBreakdown
                value={breakdown}
                onChange={setBreakdown}
                expectedTotal={reconciliation.expectedClosingBalance}
                currencyCode="GBP"
                showQuickTotalToggle={false}
              />
            </Card>
          )}

          {/* Variance Display */}
          {hasVariance && (
            <Alert
              severity={isOverThreshold ? 'error' : 'warning'}
              icon={false}
              sx={{ py: 2 }}
            >
              <Stack spacing={1}>
                <Typography variant="textMD"
                  fontWeight={700}>
                  {t('mobile.varianceDetected', 'Variance Detected')}
                </Typography>
                <Typography variant="displayMD"
                  fontWeight={700}>
                  {variance > 0 ? '+' : ''}
                  {formatCashAmount(variance, 'GBP')}
                </Typography>
                <Typography variant="textSM">
                  {variance > 0
                    ? t('closeTill.overExpected', 'Till is over by {{amount}}', { amount: formatCashAmount(Math.abs(variance), 'GBP') })
                    : t('closeTill.underExpected', 'Till is under by {{amount}}', { amount: formatCashAmount(Math.abs(variance), 'GBP') })}
                </Typography>
                {isOverThreshold && (
                  <Typography variant="textSM"
                    color="error">
                    {t('mobile.managerApprovalRequired', 'Manager approval will be required')}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {/* Notes */}
          <TextField
            label={hasVariance ? t('mobile.explainVariance', 'Explain Variance (Required)') : t('mobile.notesOptional', 'Notes (Optional)')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required={hasVariance}
            placeholder={t('mobile.explainPlaceholder', 'Explain any variance or provide additional notes...')}
            sx={{ '& .MuiInputBase-root': { fontSize: '1rem' } }}
          />

          {/* Submit Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            disabled={loading || declaredAmount <= 0 || (hasVariance && !notes.trim())}
            startIcon={<CheckCircle />}
            sx={{
              py: 2.5,
              fontSize: '1.2rem',
              fontWeight: 700,
              bgcolor: hasVariance ? 'warning.main' : 'primary.main',
            }}
          >
            {hasVariance ? t('mobile.submitWithVariance', 'Submit with Variance') : t('mobile.submitCount', 'Submit Count')}
          </Button>
        </Stack>
      </Box>

      <MessagesSnackbar
        open={showSuccess}
        message={t('mobile.countSubmitted', 'Count submitted successfully')}
        severity="success"
        duration={3000}
        onClose={() => setShowSuccess(false)}
      />
    </PageArea>
  );
};

export default MobileCountScreen;
